import { inRange, uniq } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import range from 'lodash/range';
// eslint-disable-next-line import/no-unresolved
import { Layout, PlotData } from 'plotly.js-basic-dist';
import { CqsMetric, HistoricalRecord, RiskProductsData } from '../../../../models/RiskProducts';
import {
  computeYearsAxisData,
  filterRiskMetricsByYearRange,
  mapRiskMetricsToYearsAxisValues,
  sortMetricsDescByCalculationDate,
} from '../RiskProductsWidget.logic';
import { ACR_TICKS_KEYS, CQS_TICKS_KEYS, MIN_RISK_METRICS_AXIS_RANGE_SIZE } from './CqsChart.logic.constants';
import { plotDefaultData, plotDefaultLayout } from './CqsChart.presentational.constants';
import { PlotYAxisData, YAxisValues } from './CqsChart.types';

export const hasCqsMetrics = (cqsMetrics: CqsMetric[]): boolean => {
  const cqsValues = cqsMetrics.map((metric) => metric.cqs).filter((cqs) => cqs !== null);
  const acrValues = cqsMetrics.map((metric) => metric.acr).filter((acr) => acr !== null);
  return !(isEmpty(cqsMetrics) || isEmpty(cqsValues) || isEmpty(acrValues));
};

// ------------ Filter risk metrics ------------
export const getCqsMetrics = (riskMetrics: RiskProductsData): CqsMetric[] => {
  const cqsMetrics = riskMetrics?.data.length > 0 ? processCqsMetrics(riskMetrics.data) : [];
  return sortMetricsDescByCalculationDate(cqsMetrics);
};

export const processCqsMetrics = (riskMetrics: HistoricalRecord[]): CqsMetric[] => {
  const filteredRiskMetrics = filterRiskMetricsByYearRange(riskMetrics);
  return removeUnexpectedRiskMetrics(filteredRiskMetrics);
};

const isCqsValid = (cqs: number): boolean => {
  return inRange(cqs, 0, 101);
};

const mapAcrValue = (acr: string): number => {
  const index = ACR_TICKS_KEYS.indexOf(acr);
  return index < 0 ? -1 : index * 5;
};
const removeUnexpectedRiskMetrics = (allRiskMetrics: HistoricalRecord[]): CqsMetric[] => {
  const expectedRiskMetrics: CqsMetric[] = [];
  allRiskMetrics.forEach((metric) => {
    const cqsValid = metric.cqs !== null && isCqsValid(metric.cqs);
    const acrValid = metric.acr && mapAcrValue(metric.acr) > 0;
    if (cqsValid || acrValid) {
      expectedRiskMetrics.push({
        cqs: cqsValid ? metric.cqs : null,
        acr: acrValid ? metric.acr : null,
        calculationDate: metric.calculation_date,
      });
    }
  });
  return expectedRiskMetrics;
};

// ------------ Update Plot ------------

export const composePlotParams = (
  filteredRiskMetrics: CqsMetric[],
  plotData?: Partial<PlotData>[] | null,
  plotLayout?: Partial<Layout>
): [Partial<PlotData>[], Partial<Layout>] => {
  const updatedPlotData: Partial<PlotData>[] = plotData
    ? cloneDeep<Partial<PlotData>[]>(plotData)
    : cloneDeep<Partial<PlotData>[]>(plotDefaultData);
  const updatedPlotLayout: Partial<Layout> = plotLayout
    ? cloneDeep<Partial<Layout>>({ ...plotDefaultLayout, ...plotLayout })
    : cloneDeep<Partial<Layout>>(plotDefaultLayout);

  const yAxisValues = mapRiskMetricsToYAxisValues(filteredRiskMetrics);
  const cqsAxisValues = yAxisValues.cqs;
  const acrAxisValues = yAxisValues.acr;
  const yearAxisValues = mapRiskMetricsToYearsAxisValues(filteredRiskMetrics);
  updatedPlotData[0].x = yearAxisValues;
  updatedPlotData[0].y = cqsAxisValues;

  updatedPlotData[1].x = yearAxisValues;
  updatedPlotData[1].y = acrAxisValues;

  if (!updatedPlotLayout.xaxis?.tickvals) {
    updatedPlotLayout.xaxis = {
      ...updatedPlotLayout.xaxis,
      ...computeYearsAxisData(filteredRiskMetrics[0].calculationDate),
    };
  }

  const yaxisData = computeYAxisData(cqsAxisValues, acrAxisValues);

  updatedPlotLayout.yaxis = {
    ...updatedPlotLayout.yaxis,
    ...yaxisData.cqs,
  };
  updatedPlotLayout.yaxis2 = {
    ...updatedPlotLayout.yaxis2,
    ...yaxisData.acr,
  };

  return [updatedPlotData, updatedPlotLayout];
};

// ------------ Risk Metrics Axis Block ------------
const mapRiskMetricsToYAxisValues = (riskMetrics: CqsMetric[]): YAxisValues => {
  const cqsAxisValues: number[] = [];
  const acrAxisValues: number[] = [];
  for (let i = riskMetrics.length - 1; i >= 0; i -= 1) {
    const riskMetric = riskMetrics[i];
    const cqsTickValue: number = riskMetric.cqs !== null ? riskMetric.cqs : -1;
    const acrAxisValue: number = riskMetric.acr ? mapAcrValue(riskMetric.acr) : -1;
    if (cqsTickValue >= 0 && acrAxisValue >= 0) {
      cqsAxisValues.unshift(cqsTickValue);
      acrAxisValues.unshift(acrAxisValue);
    } else if (cqsAxisValues.length && acrAxisValues.length) {
      cqsAxisValues.unshift(cqsTickValue >= 0 ? cqsTickValue : cqsAxisValues[0]);
      acrAxisValues.unshift(acrAxisValue >= 0 ? acrAxisValue : acrAxisValues[0]);
    }
  }
  return { cqs: cqsAxisValues, acr: acrAxisValues };
};

const computeYAxisData = (cqsAxisValues: number[], acrAxisValues: number[]): PlotYAxisData => {
  const combinedYAxisValues = [...cqsAxisValues, ...acrAxisValues];
  const cqsTickvals = computeYAxisTickValues(combinedYAxisValues);
  const formatedCqsTickvals = addStartEndRanges(computeCqsTickVals(cqsTickvals));
  const acrTickvals = computeAcrTickVals(formatedCqsTickvals);
  const combinedRange = computeYAxisValuesRange(formatedCqsTickvals);
  const cqsTicktext = computeYAxisCqsTexts(formatedCqsTickvals);
  const acrTicktext = computeYAxisAcrTexts(acrTickvals);

  return {
    cqs: {
      tickvals: formatedCqsTickvals,
      ticktext: cqsTicktext,
      range: combinedRange,
    },
    acr: {
      tickvals: acrTickvals,
      ticktext: acrTicktext,
      range: combinedRange,
    },
  };
};

const computeYAxisTickValues = (yAxisValues: number[]): number[] => {
  if (!yAxisValues.length) {
    return [];
  }

  const minYAxisTickValue = 0;
  const values = range(Math.min(...yAxisValues), Math.max(...yAxisValues) + 1);
  while (values.length < MIN_RISK_METRICS_AXIS_RANGE_SIZE) {
    const highestValue = values[values.length - 1];
    if (highestValue < CQS_TICKS_KEYS[CQS_TICKS_KEYS.length - 1]) {
      values.push(highestValue + 1);
    }

    if (values.length >= MIN_RISK_METRICS_AXIS_RANGE_SIZE) {
      break;
    }

    const lowestValue = values[0];
    if (lowestValue > minYAxisTickValue) {
      values.unshift(lowestValue - 1);
    }
  }
  return values;
};

const computeYAxisAcrTexts = (yAxisTickValues: number[]): string[] =>
  yAxisTickValues.map((value) => mapTickToAcr(value));

const addStartRange = (yAxisTickValues: number[]): number[] => {
  const minValue = Math.min(...yAxisTickValues);
  if (minValue > 0) {
    yAxisTickValues.unshift(Math.min(...yAxisTickValues) - 5);
  }
  return yAxisTickValues;
};

const addEndRange = (yAxisTickValues: number[]): number[] => {
  const maxValue = Math.max(...yAxisTickValues);
  if (maxValue < 100) {
    yAxisTickValues.push(Math.max(...yAxisTickValues) + 5);
  }
  return yAxisTickValues;
};

const addStartEndRanges = (yAxisTickValues: number[]): number[] => {
  addStartRange(yAxisTickValues);
  addEndRange(yAxisTickValues);

  while (yAxisTickValues.length < MIN_RISK_METRICS_AXIS_RANGE_SIZE) {
    addStartRange(yAxisTickValues);

    if (yAxisTickValues.length >= MIN_RISK_METRICS_AXIS_RANGE_SIZE) {
      break;
    }
    addEndRange(yAxisTickValues);
  }
  return yAxisTickValues;
};
const computeYAxisCqsTexts = (yAxisTickValues: number[]): string[] => yAxisTickValues.map((value) => value.toString());
const computeCqsTickVals = (yAxisTickValues: number[]): number[] => yAxisTickValues.filter((value) => value % 5 === 0);

const computeAcrTickVals = (yAxisTickValues: number[]): number[] => uniq(yAxisTickValues);

const computeYAxisValuesRange = (yAxisTickValues: number[]): number[] => {
  // 5 is added or removed from range value to show lowest and highest chart lines
  return [Math.min(...yAxisTickValues) - 5, Math.max(...yAxisTickValues) + 5];
};

const mapTickToAcr = (tick: number): string => {
  return ACR_TICKS_KEYS[Math.floor(tick / 5)];
};
